import {
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonSpinner,
} from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { IconCircleCheckFilled, IconPointFilled } from "@tabler/icons-react";
import { FormattedMessage } from "react-intl";
import Onboarding from "../onboarding";
import { NAV_ITEMS, EXPERIENCE_ITEMS } from "../menu";
import { IconMessage, IconRocket } from "@tabler/icons-react";
import { useAppStore } from "../../../store/app";
import AiApi from "../../../api/ai";
import styles from "./styles.module.css";
import { useCrawlingData } from "../../../store/crawling";
import Alert from "../../common/alert";
const AiHome: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const isOnboarded = useAppStore((state) => state.settings.onboarded);
  const onboardingState = useAppStore((state) => state.settings.onboarding);
  const onboarding = useAppStore((state) => state.settings.onboardingList);
  const channelInfo = onboarding.find((c) => c.name === "channel");
  const isOnboardingTransitioning =
    isOnboarded &&
    (onboardingState.loading || onboardingState.creatingAgent);

  const { data, isLoading: isRequesting } = useQuery({
    queryFn: () => AiApi.getAiStatus(),
    queryKey: ["getAiStatus"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });

  const aiData: Record<string, any> | null = data?.status || null;
  const isTrained = aiData?.trained || false;
  const { fullCrawl } = useCrawlingData();
  const { isScanning } = fullCrawl;
  const intl = useIntl();
  const isChannelConnected = channelInfo && channelInfo.done;

  return (
    <IonPage id="main-content">
      {isOnboarded && (
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              <FormattedMessage id="nav.ai" />
            </IonTitle>
          </IonToolbar>
        </IonHeader>
      )}
      <IonContent fullscreen color="light">
        {isRequesting ? (
          <div className="p-3 flex-align-center justify-content-center">
            <IonSpinner name="lines-sharp"></IonSpinner>
          </div>
        ) : (
          <div className={`view-wrapper ${!isOnboarded ? "h-100" : ""}`}>
            {!isOnboarded || isOnboardingTransitioning ? (
              <Onboarding />
            ) : (
              <>
                {isScanning ? (
                  <div className="view-pane">
                    <Alert
                      type="info"
                      message={intl.formatMessage({
                        id: "ai.sources.website.scanning.info",
                      })}
                      className="p-2"
                    />
                  </div>
                ) : null}
                <div className="view-pane-title mt-4">
                  {isChannelConnected ? null : "1. "}
                  <FormattedMessage
                    id={
                      isChannelConnected
                        ? "ai.sources"
                        : "ai.sources.onboarding"
                    }
                  />
                </div>

                <IonList inset={true}>
                  {NAV_ITEMS.filter((n) => n.path !== "/ai/providers").map(
                    ({ path: linkPath, label, key, icon, recommended }) => (
                      <IonItem
                        button
                        routerLink={linkPath}
                        detail={true}
                        key={key}
                      >
                        <div slot="start">{icon}</div>
                        <IonLabel>
                          <div className="flex flex-row justify-content-between align-items-center">
                            <FormattedMessage id={label} />

                            {aiData?.[key] ? null : (
                              <div className="fs-1 fw-normal-1 color-step-750">
                                <FormattedMessage
                                  id={
                                    recommended
                                      ? "common.recommended"
                                      : "common.optional"
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </IonLabel>
                        <div slot="end">
                          {aiData?.[key] ? (
                            <IconCircleCheckFilled
                              style={{
                                color: "var(--visito-color-active)",
                              }}
                            />
                          ) : null}
                        </div>
                      </IonItem>
                    )
                  )}
                </IonList>

                <div className="view-pane">
                  <span className="description fs-2 fw-normal">
                    <FormattedMessage id="ai.sources.desc" />
                  </span>
                </div>

                <div className="view-pane-title mt-4">
                  {isChannelConnected ? null : "2."}{" "}
                  <FormattedMessage
                    id={
                      isChannelConnected
                        ? "ai.playground"
                        : "ai.playground.onboarding"
                    }
                  />
                </div>

                <IonList inset={true}>
                  <IonItem
                    className={styles.animatePulseCustom}
                    button
                    routerLink={isTrained ? `/ai/playground` : undefined}
                    detail={isTrained ? true : false}
                  >
                    <div slot="start">
                      <IconMessage />
                    </div>
                    <div slot="end">
                      <IconPointFilled
                        style={{
                          color: isTrained
                            ? "var(--visito-color-active)"
                            : "var(--visito-color-danger)",
                        }}
                      />
                    </div>
                    <IonLabel>
                      <div className="flex gap-1 align-items-center">
                        <FormattedMessage id="ai.playground.test" />
                      </div>
                      <p>
                        <FormattedMessage
                          id={`ai.playground.${
                            isTrained ? "trained" : "notTrained"
                          }`}
                        />
                      </p>
                    </IonLabel>
                  </IonItem>
                </IonList>

                <div className="view-pane">
                  <span className="description fs-2 fw-normal">
                    <FormattedMessage id="ai.playground.desc" />
                  </span>
                </div>

                {!isChannelConnected ? (
                  <>
                    <div className="view-pane-title mt-4">
                      {isChannelConnected ? null : "3."}{" "}
                      <FormattedMessage id="nav.connect" />
                    </div>

                    <IonList inset={true}>
                      <IonItem
                        className={styles.animatePulseCustom}
                        button
                        routerLink={`/connect`}
                        detail={isTrained ? true : false}
                      >
                        <div slot="start">
                          <IconRocket />
                        </div>
                        <div slot="end">
                          <IconPointFilled
                            style={{
                              color: "var(--visito-color-danger)",
                            }}
                          />
                        </div>
                        <IonLabel>
                          <div className="flex gap-1 align-items-center">
                            <FormattedMessage id="nav.connect" />
                          </div>
                          <p>
                            <FormattedMessage id="ai.connect.status" />
                          </p>
                        </IonLabel>
                      </IonItem>
                    </IonList>
                    <div className="view-pane">
                      <span className="description fs-2 fw-normal">
                        <FormattedMessage id="ai.connect.desc" />
                      </span>
                    </div>
                  </>
                ) : null}

                <div className="view-pane-title mt-4">
                  <FormattedMessage id={"ai.sidebar.behavior"} />
                </div>

                <IonList inset={true}>
                  {EXPERIENCE_ITEMS.map(
                    ({ path: linkPath, label, key, icon }) => (
                      <IonItem
                        button
                        routerLink={linkPath}
                        detail={true}
                        key={key}
                      >
                        <div slot="start">{icon}</div>
                        <IonLabel>
                          <FormattedMessage id={label} />
                        </IonLabel>
                        <div slot="end">
                          {aiData?.[key] ? (
                            <IconCircleCheckFilled
                              style={{
                                color: "var(--visito-color-active)",
                              }}
                            />
                          ) : null}
                        </div>
                      </IonItem>
                    )
                  )}
                </IonList>
              </>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

interface ILayoutProps {}

export default AiHome;
